import { FC, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router';
import { ActionsPanel, AiProcessingFailedDialog, PdfViewer, PdfViewerRef, Sidebar } from './components/';
import { GdprControlsProvider } from './GdprControlsContext';
import { ColumnSizeProvider } from './ColumnSizeContext';
import { Header } from '@/ui/templates';
import { useLazyGetDocumentQuery } from '@/api';
import { useCaseCloningLock } from '@/hooks';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectPageData, clearDocument, setCurrentPageIndex, selectMetadata } from '@/store/slices/gdpr';

const GdprCheckPage: FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const dispatch = useAppDispatch();
  const pages = useAppSelector(selectPageData);
  const metadata = useAppSelector(selectMetadata);
  const [getDocument, { data: documents }] = useLazyGetDocumentQuery();
  const hasAutoScrolledToFirstLabeledPageRef = useRef(false);

  // The URL only has the document, so this waits for the document's case to load
  useCaseCloningLock(metadata?.caseId);

  // Tab title: 'GDPR Check - {caseReference}' once document metadata loads.
  // Shared useRouteTitle hook doesn't handle /gdpr so it falls through to 'Mermaidd' — set here instead.
  useEffect(() => {
    if (metadata?.caseReference) {
      document.title = `GDPR Check - ${metadata.caseReference}`;
    } else {
      document.title = 'GDPR Check';
    }
    return () => {
      document.title = 'Mermaidd';
    };
  }, [metadata?.caseReference]);

  const pdfViewerRef = useRef<PdfViewerRef>(null);

  // Load document on mount or when documentId changes
  useEffect(() => {
    if (documentId) {
      hasAutoScrolledToFirstLabeledPageRef.current = false;
      dispatch(clearDocument());
      getDocument({ document_id: documentId });
    }
  }, [documentId, dispatch, getDocument]);

  // Jump to first page with a label when document loads
  useEffect(() => {
    if (!pages || pages.length === 0 || hasAutoScrolledToFirstLabeledPageRef.current) {
      return;
    }

    const firstLabeledPageIndex = pages.findIndex(
      (page) => page.detectedBlank || page.detectedBreach || page.confirmedBlank || page.confirmedBreach,
    );

    if (firstLabeledPageIndex !== -1) {
      dispatch(setCurrentPageIndex(firstLabeledPageIndex));
      pdfViewerRef.current?.scrollToPage(firstLabeledPageIndex);
    }

    hasAutoScrolledToFirstLabeledPageRef.current = true;
  }, [pages, dispatch]);

  // Handle page click from modal (convert page number to index)
  const handlePageClick = useCallback(
    (pageNumber: number) => {
      if (pages && pageNumber > 0 && pageNumber <= pages.length) {
        const pageIndex = pageNumber - 1; // Convert to 0-based index
        dispatch(setCurrentPageIndex(pageIndex));
        pdfViewerRef.current?.scrollToPage(pageIndex);
      }
    },
    [dispatch, pages],
  );

  return (
    <ColumnSizeProvider>
      <GdprControlsProvider>
        <div className="relative flex h-screen w-full flex-col bg-[#EEE]">
          {/* Header with existing navigation (Cases, Medical Records, Users) */}
          {/* Fixed at 48px (h-12) with shrink-0 so the nav bar doesn't get squished by sibling content */}
          <div className="h-12 w-full shrink-0">
            <Header />
          </div>

          {/*
          Body row: main column (actions panel + PDF viewer) + 10vw right sidebar.
          Sidebar holds case-reference + document name (moved out of ActionsPanel).
      */}
          <div className="flex w-full flex-1 flex-row overflow-hidden">
            <div className="flex flex-1 flex-col">
              {/* GDPR Actions Panel — fixed at 48px (h-12). Inner panel uses border-y (2px) + py-[7px] (14px) so the
              32px button group fits exactly: 2 + 14 + 32 = 48. shrink-0 prevents the PDF viewer below from squishing it. */}
              <div className="h-12 w-full shrink-0">
                <ActionsPanel />
              </div>

              {/* GDPR PDF Viewer */}
              <div className="relative w-full flex-1 bg-[#EEE]">{documents && <PdfViewer ref={pdfViewerRef} />}</div>
            </div>

            <Sidebar onPageClick={handlePageClick} />
          </div>

          {/* One-shot popup that warns the user when the document's AI processing failed
          (replaces the old red top banner). Reads metadata from the gdpr slice so it
          fires automatically on mount/route-change once the doc loads. */}
          <AiProcessingFailedDialog />
        </div>
      </GdprControlsProvider>
    </ColumnSizeProvider>
  );
};

export { GdprCheckPage };
